import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMChatBV } from '@ltvco/refresh-lib/hooks';
import { useContext, useState } from 'react';

import {
  FUNCTION_CALL_OUTPUT,
  GENERIC_ERROR_MESSAGE,
  LOADING_STATUS,
  RESPONSE_TYPE,
  UNSUPPORTED_SEARCH_ERROR_MESSAGE,
  USE_OPENAI_LOCAL_CONFIGURATION,
} from 'components/ChatBV/constants';
import { sendMessageToResponsesAPI } from 'components/ChatBV/services';
import {
  GenericLLMResponse,
  LoadingStatus,
  Message,
  PersonTeaserCondensedResult,
  UserOrientedResponse,
  GenericLLMRequest,
  GenericLLMRequestItem,
  PersonParamsFunctionArguments,
  GeneratedReport,
  GenericReportResponse,
  LLMRequestItem,
  ReponsesAPIRequestProps,
} from 'components/ChatBV/types';
import {
  mapLLMServiceResponseToGenericLLMResponse,
  mapResponsesAPIResponseToProcessedResponse,
  handleFunctionCalls,
  buildLLMOrientedRequest,
} from 'components/ChatBV/utils';

export const useChatConversation = () => {
  const [userOrientedMessages, setUserOrientedMessages] = useState<Message[]>(
    []
  );
  const [lastContextId, setLastContextId] = useState<string | undefined>(
    undefined
  );
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>(
    []
  );
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    LOADING_STATUS.IDLE
  );

  const { logError } = useContext(AppConfig);

  const searchChatBV = useLLMChatBV({
    shouldSuspendExecution: false,
  });

  const sendMessageToLLM = async (
    request: GenericLLMRequest
  ): Promise<GenericLLMResponse> => {
    const llmOrientedRequest = buildLLMOrientedRequest(request);

    if (USE_OPENAI_LOCAL_CONFIGURATION) {
      const response = await sendMessageToResponsesAPI(
        llmOrientedRequest as ReponsesAPIRequestProps,
        logError
      );

      if (response.type === RESPONSE_TYPE.RESPONSE && response?.data) {
        return mapResponsesAPIResponseToProcessedResponse(response.data);
      }
      return {
        context_id: '',
        data: [response.error?.message || GENERIC_ERROR_MESSAGE, null],
        response_id: '',
      };
    }

    const response = await searchChatBV.mutateAsync(
      llmOrientedRequest as LLMRequestItem
    );

    if (response) {
      return mapLLMServiceResponseToGenericLLMResponse(response);
    }
    return {
      context_id: '',
      data: [GENERIC_ERROR_MESSAGE, null],
      response_id: '',
    };
  };

  const handleConversation = async (
    request: GenericLLMRequest
  ): Promise<UserOrientedResponse> => {
    let currentRequestItems: GenericLLMRequestItem[] = [...request.items];
    let previousContextId = request.context_id;
    let keepIterating = true;
    let wasThePreviousRequestAFunctionCall = false;
    const reportData: GenericReportResponse[] = [];
    const reportType: string[] = [];
    const permalink: string[] = [];
    let teaser: PersonTeaserCondensedResult[] = [];
    const params: PersonParamsFunctionArguments[] = [];
    const maxIterations = 3;
    let iterations = 0;

    while (keepIterating && iterations < maxIterations) {
      const response = await sendMessageToLLM({
        items: currentRequestItems,
        context_id: previousContextId,
      });

      const [answer, functionCalls] = response.data;

      const currentContextId = USE_OPENAI_LOCAL_CONFIGURATION
        ? response.response_id
        : response.context_id;
      // If the response has function calls, we need to process them
      if (
        functionCalls &&
        Array.isArray(functionCalls) &&
        functionCalls.length > 0
      ) {
        setLoadingStatus(LOADING_STATUS.ANALYZE);
        // We set this to true to know that the previous request was a function call, so we need to generate a function call output
        wasThePreviousRequestAFunctionCall = true;

        // Here we handle the function calls, triggering the Core calls and getting the data
        const processedFunctionCalls = await handleFunctionCalls(functionCalls);
        setLoadingStatus(LOADING_STATUS.PREPARING);
        currentRequestItems = [];
        for (const processedFunctionCall of processedFunctionCalls) {
          if (
            processedFunctionCall?.permalink !== undefined &&
            processedFunctionCall?.reportData !== undefined
          ) {
            setGeneratedReports((prev) => {
              // Remove existing report with same permalink if it exists
              const filteredReports = prev.filter(
                (report) => report.permalink !== processedFunctionCall.permalink
              );
              // Add the new/updated report
              return [
                ...filteredReports,
                {
                  permalink: processedFunctionCall.permalink!,
                  reportData: processedFunctionCall.reportData!,
                  type: processedFunctionCall.searchType!,
                },
              ];
            });
          }

          // Only push to arrays if permalink and reportData are defined
          if (
            processedFunctionCall?.permalink !== undefined &&
            processedFunctionCall?.reportData !== undefined
          ) {
            reportData.push(processedFunctionCall.reportData!);
            reportType.push(processedFunctionCall.searchType!);
            permalink.push(processedFunctionCall.permalink!);
          }

          // If the function call has a teaser, we need to add it to the teaser array
          if (processedFunctionCall?.teaser) {
            teaser = teaser.concat(processedFunctionCall.teaser);
            reportType.push(processedFunctionCall.searchType!);
          }

          // If the function call has a params, we need to add it to the params array
          if (processedFunctionCall?.params) {
            params.push(processedFunctionCall.params);
          }

          currentRequestItems.push({
            type: FUNCTION_CALL_OUTPUT,
            call_id: processedFunctionCall.call_id,
            output: processedFunctionCall.output,
            role: null,
            content: null,
          });
        }
        previousContextId = currentContextId;
        iterations++;
        continue;
      } else {
        keepIterating = false;
        setLastContextId(currentContextId);
        setLoadingStatus(LOADING_STATUS.FINISHING);

        if (
          wasThePreviousRequestAFunctionCall &&
          reportData.length > 0 &&
          reportType.length > 0 &&
          permalink.length > 0
        ) {
          return {
            context_id: currentContextId,
            message: answer as string,
            reports: permalink.map((permalink, index) => ({
              reportData: reportData[index],
              type: reportType[index],
              permalink,
            })),
            response_id: response.response_id,
          };
        } else if (wasThePreviousRequestAFunctionCall && teaser.length > 0) {
          return {
            context_id: currentContextId,
            message: answer as string,
            teaser,
            params: params[0],
            response_id: response.response_id,
          };
        }
        return {
          context_id: currentContextId,
          message: answer as string,
          response_id: response.response_id,
        };
      }
    }

    return {
      context_id: '',
      message: UNSUPPORTED_SEARCH_ERROR_MESSAGE,
      response_id: '',
    };
  };

  return {
    userOrientedMessages,
    setUserOrientedMessages,
    generatedReports,
    handleConversation,
    lastContextId,
    setLastContextId,
    loadingStatus,
    setLoadingStatus,
  };
};
